import React, { useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckOutlined, DeleteOutlined, EditOutlined, InboxOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import { getCurrentUserId, isAdmin } from '../../utils/roles';
import {
  useArchiveTeamAssistantMemory,
  useConfirmTeamAssistantMemory,
  useCreateTeamAssistantMemory,
  useDeleteTeamAssistantMemory,
  useTeamAssistantMemories,
  useUnarchiveTeamAssistantMemory,
  useUpdateTeamAssistantMemory,
} from '../../hooks/useTeamAssistantQueries';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const statusColor = {
  candidate: 'gold',
  confirmed: 'green',
  archived: 'default',
};

const TeamAssistantMemoriesPanel = ({ currentUser, enabled = true }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [editOpen, setEditOpen] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const admin = isAdmin(currentUser);
  const userId = getCurrentUserId(currentUser);

  const memoriesQuery = useTeamAssistantMemories(statusFilter, { enabled });
  const createMutation = useCreateTeamAssistantMemory();
  const confirmMutation = useConfirmTeamAssistantMemory();
  const archiveMutation = useArchiveTeamAssistantMemory();
  const updateMutation = useUpdateTeamAssistantMemory();
  const deleteMutation = useDeleteTeamAssistantMemory();
  const unarchiveMutation = useUnarchiveTeamAssistantMemory();

  const onAdd = async (values) => {
    try {
      await createMutation.mutateAsync({
        memory: values.memory,
        category: values.category || undefined,
      });
      message.success(
        admin
          ? 'Saved as candidate — scroll down and click Confirm. It is not active until then.'
          : 'Submitted for admin review — not active for the team until an admin confirms.'
      );
      form.resetFields();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Could not save memory');
    }
  };

  const items = memoriesQuery.data || [];

  const canEdit = (m) =>
    admin ||
    (m.status === 'candidate' &&
      m.created_by?.id != null &&
      String(m.created_by.id) === String(userId));

  const canDelete = (m) => canEdit(m);

  const openEdit = (m) => {
    setEditingMemory(m);
    editForm.setFieldsValue({
      memory: m.memory,
      category: m.category || '',
    });
    setEditOpen(true);
  };

  const onEditSubmit = async (values) => {
    if (!editingMemory) return;
    try {
      await updateMutation.mutateAsync({
        id: editingMemory.id,
        memory: values.memory,
        category: values.category || undefined,
      });
      message.success('Memory updated');
      setEditOpen(false);
      setEditingMemory(null);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Could not update');
    }
  };

  return (
    <div className="team-assistant-memories-panel">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Approval required"
        description={
          admin
            ? 'New saves are always candidates first. The assistant only uses facts after you click Confirm on each row. Archive hides a fact; Unarchive restores it. Delete removes it permanently.'
            : 'Your saves stay as candidates until an admin confirms them. The assistant may still answer from normal chat history — that is not the same as team memory.'
        }
      />
      <div className="team-assistant-kb-tab-intro">
        <strong>Team memory</strong> stores short, vetted insurance facts your agency wants every assistant
        reply to respect — for example appointment rules, how you quote a line, or a carrier nuance that is
        not in a PDF. <strong>Confirmed</strong> memories are added to the system prompt on each message.{' '}
        <strong>Candidates</strong> wait for admin approval so bad or outdated facts do not spread. This is
        separate from uploading files (see <strong>Files</strong>) and from automatic search over old chat
        (see <strong>Deep search</strong> / Overview metrics).
      </div>

      <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
        To tie a memory to a specific chat reply, use the <strong>bookmark</strong> icon under that message
        — the app links it for you. Use the form below for general facts that are not tied to one bubble.
      </Paragraph>

      <Form form={form} layout="vertical" onFinish={onAdd}>
        <dl className="team-assistant-memories-field-guide">
          <dt>Team fact (required)</dt>
          <dd>
            The one sentence the assistant should always know — carrier rule, LOB note, commission policy,
            etc. Admins must confirm before it affects replies.
          </dd>
          <dt>Category (optional)</dt>
          <dd>
            A short label so your team can sort memories — e.g. <Text code>Carriers</Text>,{' '}
            <Text code>LOB</Text>, <Text code>Commissions</Text>, <Text code>Underwriting</Text>. It appears
            as a tag in this list and in the context sent to the assistant.
          </dd>
          <dt>Link to chat</dt>
          <dd>
            You never need a message number. Open team chat and click the bookmark on the message — the link
            is saved automatically for admins to review.
          </dd>
        </dl>

        <Form.Item
          name="memory"
          label="Team fact"
          rules={[{ required: true, message: 'Enter the fact to remember' }]}
        >
          <TextArea
            rows={3}
            placeholder="e.g. For ABC Mutual workers comp in TX, minimum premium applies before binding…"
          />
        </Form.Item>
        <div className="team-assistant-memories-optional-row team-assistant-memories-optional-row--two">
          <Form.Item
            name="category"
            label="Category (optional)"
            tooltip="Tag for grouping — Carriers, LOB, Commissions, Process…"
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="Carriers" />
          </Form.Item>
          <Form.Item label=" " style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={createMutation.isPending}
            >
              Add candidate
            </Button>
          </Form.Item>
        </div>
      </Form>

      <div className="team-assistant-memories-toolbar">
        <Text strong>Saved memories</Text>
        <Select
          size="small"
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'candidate', label: 'Candidates' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
      </div>

      {memoriesQuery.isLoading ? (
        <div className="team-assistant-memories-loading">
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty description="No team memories yet — add a carrier or process fact above" />
      ) : (
        <ul className="team-assistant-memories-list">
          {items.map((m) => (
            <li key={m.id} className="team-assistant-memories-item">
              <div className="team-assistant-memories-item-head">
                <Tag color={statusColor[m.status] || 'default'}>{m.status}</Tag>
                {m.category && <Tag>{m.category}</Tag>}
              </div>
              <Paragraph style={{ marginBottom: 8 }}>{m.memory}</Paragraph>
              <Text type="secondary" className="team-assistant-memories-meta">
                {m.source_message_id ? 'Linked from a team chat message' : 'Added manually (not linked to chat)'}
                {admin && m.source_message_id ? ` · ref #${m.source_message_id}` : ''}
                {m.confirmed_by?.name ? ` · Confirmed by ${m.confirmed_by.name}` : ''}
                {m.created_by?.name ? ` · Added by ${m.created_by.name}` : ''}
              </Text>
              <div className="team-assistant-memories-item-actions">
                <Space size="small" wrap>
                {canEdit(m) && (
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(m)}>
                    Edit
                  </Button>
                )}
                {admin && m.status !== 'archived' && m.status === 'candidate' && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={confirmMutation.isPending}
                    onClick={() =>
                      confirmMutation.mutate(m.id, {
                        onSuccess: () =>
                          message.success('Memory confirmed — now used in assistant replies'),
                        onError: (e) =>
                          message.error(e?.response?.data?.message || 'Confirm failed'),
                      })
                    }
                  >
                    Confirm
                  </Button>
                )}
                {admin && m.status === 'archived' && (
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    loading={unarchiveMutation.isPending}
                    onClick={() =>
                      unarchiveMutation.mutate(m.id, {
                        onSuccess: (row) => {
                          const label =
                            row?.status === 'confirmed' ? 'confirmed' : 'candidate';
                          message.success(`Memory restored as ${label}`);
                        },
                        onError: (e) =>
                          message.error(e?.response?.data?.message || 'Restore failed'),
                      })
                    }
                  >
                    Unarchive
                  </Button>
                )}
                {admin && m.status !== 'archived' && (
                  <Popconfirm
                    title="Archive this memory?"
                    description="It will no longer appear in assistant context. You can still see it under Archived."
                    onConfirm={() =>
                      archiveMutation.mutate(m.id, {
                        onSuccess: () => message.success('Archived'),
                        onError: (e) =>
                          message.error(e?.response?.data?.message || 'Archive failed'),
                      })
                    }
                  >
                    <Button size="small" icon={<InboxOutlined />}>
                      Archive
                    </Button>
                  </Popconfirm>
                )}
                {canDelete(m) && (
                  <Popconfirm
                    title={admin ? 'Delete permanently?' : 'Withdraw this candidate?'}
                    description="This cannot be undone."
                    onConfirm={() =>
                      deleteMutation.mutate(m.id, {
                        onSuccess: () =>
                          message.success(admin ? 'Deleted permanently' : 'Withdrawn'),
                        onError: (e) =>
                          message.error(e?.response?.data?.message || 'Delete failed'),
                      })
                    }
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                      {admin ? 'Delete permanently' : 'Withdraw'}
                    </Button>
                  </Popconfirm>
                )}
                </Space>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        title="Edit team memory"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingMemory(null);
        }}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEditSubmit}>
          <Form.Item
            name="memory"
            label="Team fact"
            rules={[{ required: true, message: 'Enter the fact' }]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="category" label="Category (optional)">
            <Input placeholder="Carriers" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamAssistantMemoriesPanel;
